// src/views/DomainesExpertiseList.js
import React, { useState, useEffect } from 'react';
import { domainesExpertiseAPI, formationsAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';

function DomainesExpertiseList() {
  const [domaines, setDomaines] = useState([]);
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentDomaine, setCurrentDomaine] = useState({ id: null, titre: '' });
  const [isEditing, setIsEditing] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [domainesData, formationsData] = await Promise.all([
        domainesExpertiseAPI.getAll(),
        formationsAPI.getAll()
      ]);
      setDomaines(domainesData);
      setFormations(formationsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDomaine = async (domaineData) => {
    try {
      const response = await domainesExpertiseAPI.create(domaineData);
      setDomaines([...domaines, response]);
      setShowModal(false);
      setCurrentDomaine({ id: null, titre: '' });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateDomaine = async (id, domaineData) => {
    try {
      const response = await domainesExpertiseAPI.update(id, domaineData);
      setDomaines(domaines.map(d => d.id === id ? response : d));
      setShowModal(false);
      setCurrentDomaine({ id: null, titre: '' });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteDomaine = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce module ?')) {
      return;
    }

    try {
      await domainesExpertiseAPI.remove(id);
      setDomaines(domaines.filter(d => d.id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  const openCreateModal = () => {
    setCurrentDomaine({ id: null, titre: '' });
    setIsEditing(false);
    setShowModal(true);
  };

  const openEditModal = (domaine) => {
    setCurrentDomaine({ id: domaine.id, titre: domaine.titre });
    setIsEditing(true);
    setShowModal(true);
  };

  if (loading) return <div className="container mx-auto px-4 py-8">Chargement...</div>;
  if (error) return <div className="container mx-auto px-4 py-8">Erreur: {error}</div>;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Modules</h1>
        <button
          onClick={openCreateModal}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Créer un module
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead>
            <tr>
              <th className="py-2 px-4 bg-gray-200 text-left">ID</th>
              <th className="py-2 px-4 bg-gray-200 text-left">Titre</th>
              <th className="py-2 px-4 bg-gray-200 text-left">Date de création</th>
              <th className="py-2 px-4 bg-gray-200 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {domaines.map((domaine) => (
              <tr key={domaine.id}>
                <td className="border px-4 py-2">{domaine.id}</td>
                <td className="border px-4 py-2">{domaine.titre}</td>
                <td className="border px-4 py-2">{new Date(domaine.created_at).toLocaleDateString()}</td>
                <td className="border px-4 py-2">
                  <button
                    onClick={() => openEditModal(domaine)}
                    className="bg-yellow-500 hover:bg-yellow-700 text-white py-1 px-2 rounded mr-2"
                  >
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDeleteDomaine(domaine.id)}
                    className="bg-red-500 hover:bg-red-700 text-white py-1 px-2 rounded"
                  >
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal pour créer/modifier un module */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {isEditing ? 'Modifier le module' : 'Créer un module'}
              </h3>
              
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="titre">
                  Titre
                </label>
                <input
                  id="titre"
                  type="text"
                  value={currentDomaine.titre}
                  onChange={(e) => setCurrentDomaine({...currentDomaine, titre: e.target.value})}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                />
              </div>
              
              
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    if (isEditing) {
                      handleUpdateDomaine(currentDomaine.id, currentDomaine);
                    } else {
                      handleCreateDomaine(currentDomaine);
                    }
                  }}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  {isEditing ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DomainesExpertiseList;